import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Modal, Form, Button, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { adminService } from '../../services/adminService';
import toast from 'react-hot-toast';
import SeatLayoutVisualizer from './SeatLayoutVisualizer';

interface AddBookingModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
}

interface Show {
  id: string; // This is the Vista session/show ID
  vistaShowId?: string; // Optional: explicit Vista show ID if provided
  cinemaId: string;
  movieId: string;
  showDate: string;
  showTime: string;
  screenName: string;
  screenId: string;
  price: number | Record<string, { price: number; ticketTypeCode: string; ticketTypeDescription: string; areaCode: string; areaName: string }>; // Can be object (area-wise) or number
  areaPricing?: Array<{ areaCode: string; areaName: string; price: number; ticketTypeCode: string; ticketTypeDescription: string }>;
  language: string;
  screenType: string;
  available: boolean;
}

const AddBookingModal: React.FC<AddBookingModalProps> = ({ show, onHide, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadingShows, setLoadingShows] = useState(false);
  const [loadingSeatLayout, setLoadingSeatLayout] = useState(false);
  const [initializingBooking, setInitializingBooking] = useState(false);
  const [vistaTransactionId, setVistaTransactionId] = useState<string | null>(null);
  const [loadingCinemaMovies, setLoadingCinemaMovies] = useState(false);
  const [cinemas, setCinemas] = useState<any[]>([]);
  const [movies, setMovies] = useState<any[]>([]);
  const [cinemaMovies, setCinemaMovies] = useState<any[]>([]);
  const [shows, setShows] = useState<Show[]>([]);
  const [seatLayout, setSeatLayout] = useState<any>(null);
  const [selectedSeats, setSelectedSeats] = useState<string[]>([]);
  const [quantity, setQuantity] = useState<number | null>(null);
  const [isConfirmingSeats, setIsConfirmingSeats] = useState(false);
  const [seatsConfirmed, setSeatsConfirmed] = useState(false);
  const [formData, setFormData] = useState({
    cinemaId: '',
    movieId: '',
    showDate: new Date().toISOString().split('T')[0],
    showId: '',
    screenName: '',
    seats: '',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    ticketPrice: '',
  });

  useEffect(() => {
    if (show) {
      loadCinemas();
      loadMovies();
      // Reset form when modal opens
      setFormData({
        cinemaId: '',
        movieId: '',
        showDate: new Date().toISOString().split('T')[0],
        showId: '',
        screenName: '',
        seats: '',
        customerName: '',
        customerEmail: '',
        customerPhone: '',
        ticketPrice: '',
      });
      setCinemaMovies([]);
      setShows([]);
      setSeatLayout(null);
      setSelectedSeats([]);
      setVistaTransactionId(null);
      setQuantity(null);
      setSeatsConfirmed(false);
    }
  }, [show]);

  // Load movies for selected cinema
  useEffect(() => {
    if (formData.cinemaId && formData.showDate) {
      loadCinemaMovies();
    } else {
      setCinemaMovies([]);
      setFormData(prev => ({ ...prev, movieId: '', showId: '', screenName: '', ticketPrice: '' }));
      setShows([]);
      setSeatLayout(null);
      setSelectedSeats([]);
    }
  }, [formData.cinemaId, formData.showDate]);

  // Load shows when cinema, movie, and date are selected
  useEffect(() => {
    if (formData.cinemaId && formData.movieId && formData.showDate) {
      loadShows();
    } else {
      setShows([]);
      setFormData(prev => ({ ...prev, showId: '', screenName: '', ticketPrice: '' }));
      setSeatLayout(null);
      setSelectedSeats([]);
    }
  }, [formData.cinemaId, formData.movieId, formData.showDate]);

  // Define the seat layout structure type
  interface AdminSeatRow {
    row: string;
    seats: any[];
    gridRowId?: number;
    areaCode?: string;
    areaName?: string;
    seatY?: number;
  }

  // Transform Vista seat layout to format expected by SeatLayoutVisualizer (same as website)
  // Accept selectedSeats as parameter to avoid dependency on state
  const transformVistaSeatLayoutForAdmin = useCallback((vistaData: any, showtime?: Show | null, currentSelectedSeats: string[] = []) => {
    // Vista API returns SeatLayout (capital S), handle both cases
    const seatLayoutData = vistaData?.SeatLayout || vistaData?.seatLayout || vistaData;
    
    if (!seatLayoutData || !seatLayoutData.areas || !Array.isArray(seatLayoutData.areas)) {
      console.error('Invalid Vista seat layout data:', {
        vistaData,
        hasSeatLayout: !!vistaData?.seatLayout,
        hasSeatLayoutCapital: !!vistaData?.SeatLayout,
        areas: seatLayoutData?.areas,
      });
      return null; // Return null to trigger default layout
    }

    const seatLayout: AdminSeatRow[] = [];
    // Sort areas by areaNum like the frontend does
    const sortedAreas = [...seatLayoutData.areas].sort(
      (a, b) => Number(a.areaNum || 999) - Number(b.areaNum || 999)
    );

    sortedAreas.forEach((area: any) => {
      if (!area.rows || !Array.isArray(area.rows)) return;

      const areaCode = area.areaCode || area.areaNum || 'unknown';
      // Extract area name from various possible fields
      const areaName = area.areaName || area.area_name || area.name || area.areaDescription || area.area_description || 'Area';
      // Extract price from area or from first seat's pricing info
      const areaPrice = area.price || area.amount || area.seatPrice || area.basePrice || area.areaPrice || 200;

      area.rows.forEach((row: any) => {
        if (!row.seats || !Array.isArray(row.seats)) return;

        const rowId = row.rowPhyId || row.rowId || 'A';
        const rowSeats: any[] = [];
        let seatCounter = 1; // Sequential counter for non-empty seats only

        // Process seats in the order they come from the API to preserve rendering order
        row.seats.forEach((seat: any, seatIndexInRow: number) => {
          // Handle empty seats (isEmpty: true) as vacant spaces
          if (seat.isEmpty || seat.gridSeatNum === 0) {
            // Use index to ensure unique keys even if gridSeatNum is the same
            // Format: empty-{rowId}-{index}-{gridSeatNum} to ensure uniqueness
            rowSeats.push({
              id: `empty-${areaCode}-${rowId}-${seatIndexInRow}-${seat.gridSeatNum ?? 0}`,
              number: 0, // Empty seats don't get a number
              originalNumber: seat.gridSeatNum || 0,
              row: rowId,
              isEmpty: true,
              isBooked: false,
              isSelected: false,
              isAisle: false,
            });
            return; // Don't increment counter for empty seats
          }

          const seatId = seat.seatId || `${rowId}${seat.gridSeatNum}`;
          const isHouseOrSpecial = seat.status === 'houseOrSpecial' || seat.seatStatus === 'houseOrSpecial' || seat.houseOrSpecial === true || seat.houseOrSpecial === '1' || seat.type === 'houseOrSpecial';
          const isBooked = seat.status === 'booked' || seat.seatStatus === '1' || isHouseOrSpecial;
          
          // Use area-wise pricing from showtime - same as website
          // New structure: price[areaCode] is an object with { price, ticketTypeCode, ticketTypeDescription, areaCode, areaName }
          let price = null;
          let ticketTypeCode = null;
          let ticketTypeDescription = null;
          
          if (showtime?.price && typeof showtime.price === 'object' && areaCode && showtime.price[areaCode]) {
            const pricingInfo = showtime.price[areaCode];
            
            // Handle both old format (number) and new format (object)
            if (typeof pricingInfo === 'object' && pricingInfo !== null && pricingInfo.price !== undefined) {
              // New format: object with full pricing info
              price = pricingInfo.price;
              ticketTypeCode = pricingInfo.ticketTypeCode || null;
              ticketTypeDescription = pricingInfo.ticketTypeDescription || pricingInfo.areaName || areaName || null;
            } else if (typeof pricingInfo === 'number') {
              // Old format: just a number (backward compatibility)
              price = pricingInfo;
              // Try to get ticketTypeCode from areaPricing array if available
              if (showtime?.areaPricing && Array.isArray(showtime.areaPricing)) {
                const areaPricingInfo = showtime.areaPricing.find((ap: any) => ap.areaCode === areaCode);
                if (areaPricingInfo) {
                  ticketTypeCode = areaPricingInfo.ticketTypeCode || null;
                  ticketTypeDescription = areaPricingInfo.ticketTypeDescription || areaPricingInfo.areaName || areaName || null;
                }
              }
            }
          } else {
            // If showtime pricing not available, try areaPricing array
            if (showtime?.areaPricing && Array.isArray(showtime.areaPricing)) {
              const areaPricingInfo = showtime.areaPricing.find((ap: any) => ap.areaCode === areaCode);
              if (areaPricingInfo) {
                price = areaPricingInfo.price || areaPrice;
                ticketTypeCode = areaPricingInfo.ticketTypeCode || null;
                ticketTypeDescription = areaPricingInfo.ticketTypeDescription || areaPricingInfo.areaName || areaName || null;
              }
            }
          }
          
          // Fallback to area price if showtime pricing not available
          if (!price || price === null) {
            price = areaPrice;
          }
          
          // Ensure price is a valid number
          if (typeof price !== 'number' || isNaN(price) || price <= 0) {
            price = areaPrice || 200; // Final fallback
          }
          
          // Log warning if ticketTypeCode is missing (but don't fail - will be caught later)
          if (!ticketTypeCode && price > 0) {
            console.warn(`Missing ticketTypeCode for seat ${seatId} in area ${areaCode}. Showtime pricing:`, {
              showtimePrice: showtime?.price,
              areaPricing: showtime?.areaPricing,
              areaCode,
            });
          }
          
          // Determine category based on area code or price
          let category = 'regular';
          if (areaCode.includes('0000000003') || areaCode.includes('0000000005')) {
            category = 'vip';
          } else if (areaCode.includes('0000000004')) {
            category = 'premium';
          } else if (areaCode.includes('0000000006')) {
            category = 'regular';
          } else {
            // Fallback: categorize by price range
            if (price >= 350) {
              category = 'vip';
            } else if (price >= 250) {
              category = 'premium';
            } else {
              category = 'regular';
            }
          }

          rowSeats.push({
            id: seatId, // This is the Vista seatId (format: "0000000006|1|1|2")
            number: seat.seatNum, // Sequential number ignoring empty spaces
            originalNumber: seat.gridSeatNum, // Store original gridSeatNum for reference
            row: rowId,
            displayNumber: `${rowId}${seat.seatNum}`, // Readable format like "A1", "B2" for display
            category: category,
            price: price,
            areaCode: areaCode, // Store area code for reference
            areaName: areaName,
            ticketTypeCode: ticketTypeCode, // Store ticket type code for booking (REQUIRED)
            ticketTypeDescription: ticketTypeDescription, // Store ticket type description for booking
            isBooked: isBooked,
            isHouseOrSpecial: isHouseOrSpecial,
            isAisle: false, // Vista API doesn't provide aisle info, set to false
            isSelected: currentSelectedSeats.includes(seatId),
            vistaSeatId: seat.seatId || seatId, // Keep original Vista seat ID for reference (from API response)
            seatId: seat.seatId || seatId, // Also store as seatId for easy access
            isEmpty: false, // Explicitly mark as not empty
            seatY: seat.seatY, // Store seatY for positioning like frontend
          });
        });

        const visualSeats = [...rowSeats].reverse(); // Reverse seats like frontend does

        if (visualSeats.length > 0) {
          // Check if row already exists, if so merge seats
          // Use both rowId and areaCode for merging like frontend does
          const existingRow = seatLayout.find((r) => r.row === rowId && r.areaCode === areaCode);
          
          if (existingRow) {
            existingRow.gridRowId = row.gridRowId ?? existingRow.gridRowId;
            // Merge seats and maintain original order (no sorting)
            const allSeats = [...existingRow.seats, ...visualSeats];

            // Remove duplicates by seat id (important for merged areas)
            const uniqueSeats = [];
            const seen = new Set();

            allSeats.forEach((seat) => {
              if (!seen.has(seat.id)) {
                seen.add(seat.id);
                uniqueSeats.push(seat);
              }
            });            

            existingRow.seats = allSeats;
          } else {
            // Calculate average seatY like frontend does
            const rowY = visualSeats
              .map(s => s.seatY)
              .filter(v => v != null)
              .reduce((a, b) => a + b, 0) / visualSeats.length || 0;
            
            seatLayout.push({
              row: rowId,
              seats: visualSeats,
              gridRowId: row.gridRowId ?? 999,
              areaCode: areaCode, // Store areaCode for proper grouping
              areaName: areaName,
              seatY: rowY, // ✅ CRITICAL FIX for row positioning
            });            
          }
        }
      });
    });

    // Sort rows alphabetically like frontend does
    seatLayout.sort((a, b) =>
      (a.row || "").localeCompare(b.row || "", undefined, {
        numeric: true,
        sensitivity: "base",
      })
    );

    return seatLayout;
  }, []);

  // Default seat layout fallback (defined early so it can be used in useEffect)
  // Format: array of rows with seats (same as website)
  const loadDefaultSeatLayout = useCallback(() => {
    console.log('Loading default seat layout');
    const defaultLayout: Array<{ row: string; seats: any[] }> = [];
    
    const rows = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I'];
    rows.forEach((rowId, index) => {
      const category = index >= rows.length - 2 ? 'premium' : 'regular';
      const price = index >= rows.length - 2 ? 250 : 200;
      const seats: any[] = [];
      
      for (let i = 1; i <= 15; i++) {
        const seatId = `${rowId}${i}`;
        seats.push({
          id: seatId,
          number: i,
          row: rowId,
          displayNumber: `${rowId}${i}`,
          category: category,
          price: price,
          isBooked: false,
          isAisle: i === 8, // Aisle at position 8
          isSelected: false,
          isEmpty: false,
        });
      }
      
      defaultLayout.push({ row: rowId, seats: seats });
    });
    
    setSeatLayout(defaultLayout);
    console.log('Default seat layout set:', defaultLayout);
  }, []);

  // Find selected show (used in JSX and for seat layout loading)
  const selectedShow = useMemo(() => {
    if (!formData.showId || shows.length === 0) {
      return null;
    }
    
    // Try to find show by matching the selected showId with either id or vistaShowId
    // The dropdown value is set to sessionId (vistaShowId || id), so we need to match against that
    // IMPORTANT: API returns id and vistaShowId as numbers, but formData.showId is a string
    // So we need to convert both to strings for comparison
    const found = shows.find(s => {
      const sessionId = s.vistaShowId || s.id;
      // Convert all to strings for comparison (API returns numbers, formData has strings)
      const sIdStr = String(s.id);
      const sVistaShowIdStr = s.vistaShowId ? String(s.vistaShowId) : null;
      const sessionIdStr = sessionId ? String(sessionId) : null;
      const formShowIdStr = String(formData.showId);
      
      // Match against: id, vistaShowId, or the computed sessionId (all as strings)
      return sIdStr === formShowIdStr || 
             (sVistaShowIdStr && sVistaShowIdStr === formShowIdStr) ||
             (sessionIdStr && sessionIdStr === formShowIdStr);
    });
    
    console.log('Finding selected show:', {
      formDataShowId: formData.showId,
      formDataShowIdType: typeof formData.showId,
      showsCount: shows.length,
      showsIds: shows.map(s => ({ 
        id: s.id,
        idType: typeof s.id,
        idStr: String(s.id),
        vistaShowId: s.vistaShowId,
        vistaShowIdType: typeof s.vistaShowId,
        vistaShowIdStr: s.vistaShowId ? String(s.vistaShowId) : null,
        sessionId: s.vistaShowId || s.id,
        sessionIdStr: String(s.vistaShowId || s.id),
        screenName: s.screenName,
        price: s.price,
      })),
      found: found ? {
        id: found.id,
        vistaShowId: found.vistaShowId,
        screenId: found.screenId,
        screenName: found.screenName,
        price: found.price,
        showTime: found.showTime,
      } : null,
      matchAttempts: {
        byId: shows.find(s => String(s.id) === String(formData.showId)) ? 'MATCHED' : 'NO MATCH',
        byVistaId: shows.find(s => s.vistaShowId && String(s.vistaShowId) === String(formData.showId)) ? 'MATCHED' : 'NO MATCH',
        bySessionId: shows.find(s => String(s.vistaShowId || s.id) === String(formData.showId)) ? 'MATCHED' : 'NO MATCH',
      },
    });
    
    // If show is found, verify it has required fields
    if (found) {
      if (!found.screenName || found.screenName.trim() === '') {
        console.warn('Found show but screenName is missing:', found);
      }
      const price =
  typeof found.price === 'number'
    ? found.price
    : Object.values(found.price ?? {})[0]?.price ?? 0;

if (price <= 0) {
  console.warn('Found show but price is missing or invalid:', found);
}

    }
    
    return found || null;
  }, [formData.showId, shows]);

  // Load Vista seat layout function - defined early so it can be used in useEffect
  const loadVistaSeatLayout = useCallback(async (transId?: string) => {
    try {
      if (!selectedShow || !formData.cinemaId) {
        console.warn('Cannot load Vista seat layout: missing show or cinemaId');
        return;
      }

      // Get Vista cinema ID and session ID
      const cinema = cinemas.find(c => c.id === formData.cinemaId);
      const vistaCinemaId = cinema?.vistaCinemaId || cinema?.vistaCinemaID || cinema?.vistacinemaid;
      const vistaSessionId = selectedShow.vistaShowId || selectedShow.id;

      if (!vistaCinemaId || !vistaSessionId) {
        console.warn('Cannot load Vista seat layout: missing Vista IDs', {
          vistaCinemaId,
          vistaSessionId,
        });
        return;
      }

      // Use provided transId or get from state
      const transactionId = transId || vistaTransactionId;
      if (!transactionId) {
        console.warn('Cannot load Vista seat layout: missing transaction ID');
        return;
      }
      
      setLoadingSeatLayout(true);

      // Load seat layout from Vista API
      console.log('Loading Vista seat layout...', {
        vistaCinemaId,
        vistaSessionId,
        transactionId,
      });

      const layoutResponse = await adminService.getVistaSeatLayout(
        vistaCinemaId,
        String(vistaSessionId),
        transactionId
      );

      if (layoutResponse && layoutResponse.success && layoutResponse.data) {
        console.log('Vista seat layout loaded successfully:', layoutResponse.data);
        // Transform Vista seat layout to format expected by SeatLayoutVisualizer (same as website)
        // Pass selectedSeats as parameter to avoid dependency
        const transformedLayout = transformVistaSeatLayoutForAdmin(layoutResponse.data, selectedShow, selectedSeats);
        if (transformedLayout && transformedLayout.length > 0) {
          setSeatLayout(transformedLayout);
        } else {
          console.warn('Failed to transform Vista seat layout');
          toast.error('Failed to load seat layout');
        }
      } else {
        console.warn('Vista seat layout API response not successful:', layoutResponse);
        toast.error('Failed to load seat layout from Vista API');
      }
    } catch (error: any) {
      console.error('Error loading Vista seat layout:', error);
      toast.error(error.message || 'Failed to load seat layout from Vista API');
    } finally {
      setLoadingSeatLayout(false);
    }
  }, [selectedShow, formData.cinemaId, vistaTransactionId, cinemas, transformVistaSeatLayoutForAdmin, selectedSeats]);

  // Update screenName when show is selected (separate effect to avoid infinite loop)
  useEffect(() => {
    if (selectedShow && formData.showId) {
      const screenName = selectedShow.screenName || 'Screen';
      
      // Update formData only if screenName has changed
      setFormData(prev => {
        if (prev.screenName !== screenName) {
          return {
            ...prev,
            screenName: screenName,
          };
        }
        return prev;
      });
    }
  }, [selectedShow?.screenName, formData.showId]);

  // Initialize transaction and load seat layout when quantity is selected
  useEffect(() => {
    if (quantity && selectedShow && formData.cinemaId && formData.showId && !vistaTransactionId) {
      // Initialize Vista transaction when quantity is selected
      const initializeTransaction = async () => {
        try {
          const cinema = cinemas.find(c => c.id === formData.cinemaId);
          const vistaCinemaId = cinema?.vistaCinemaId || cinema?.vistaCinemaID || cinema?.vistacinemaid;
          const vistaSessionId = selectedShow.vistaShowId || selectedShow.id;

          if (!vistaCinemaId || !vistaSessionId) {
            toast.error('Missing Vista IDs. Please select a valid show.');
            return;
          }

          setInitializingBooking(true);
          const initResponse = await adminService.initVistaBooking(vistaCinemaId, String(vistaSessionId));
          
          if (initResponse.success && initResponse.data?.transactionId) {
            const transId = initResponse.data.transactionId;
            setVistaTransactionId(transId);
            console.log('Vista booking initialized, transaction ID:', transId);
            
            // Load seat layout after transaction is initialized
            await loadVistaSeatLayout(transId);
          } else {
            throw new Error('Failed to initialize Vista booking: ' + (initResponse.message || 'Unknown error'));
          }
        } catch (error: any) {
          console.error('Error initializing Vista transaction:', error);
          toast.error(error.message || 'Failed to initialize booking transaction');
        } finally {
          setInitializingBooking(false);
        }
      };

      initializeTransaction();
    } else if (!quantity) {
      // Clear seat layout and transaction when quantity is cleared
      setSeatLayout(null);
      setVistaTransactionId(null);
      setSelectedSeats([]);
      setSeatsConfirmed(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quantity, selectedShow?.id, formData.cinemaId, formData.showId, vistaTransactionId]);

  const loadCinemas = async () => {
    try {
      setLoading(true);
      const response = await adminService.getCinemas({ limit: 100 });
      if (response.success && response.data) {
        setCinemas(response.data);
      }
    } catch (error: any) {
      toast.error('Failed to load cinemas');
      console.error('Error loading cinemas:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMovies = async () => {
    try {
      // Load all active movies (not just "Now Showing") to include Vista-synced movies
      const response = await adminService.getMovies({ limit: 100 });
      if (response.success && response.data) {
        // Filter to show active movies (Now Showing, Coming Soon, or any with vistaMovieId)
        const activeMovies = (response.data || []).filter((movie: any) => {
          return (
            movie.status === 'Now Showing' ||
            movie.status === 'Coming Soon' ||
            movie.isActive === true ||
            movie.vistaMovieId ||
            movie.vistaMovieID
          );
        });
        setMovies(activeMovies);
      }
    } catch (error: any) {
      toast.error('Failed to load movies');
      console.error('Error loading movies:', error);
    }
  };

  const loadCinemaMovies = async () => {
    try {
      setLoadingCinemaMovies(true);
      // Fetch shows for this cinema (without movieId) to get list of movies
      const response = await adminService.getShows(
        formData.cinemaId,
        undefined, // No movieId - get all movies for this cinema
        formData.showDate
      );
      
      if (response.success && response.data) {
        // Create a map of unique movies from shows
        // Key: movieId, Value: { movieId, vistaMovieId, movieTitle, show }
        const movieMap = new Map<string, any>();
        
        (response.data || []).forEach((show: any) => {
          const movieId = show.movieId;
          const vistaMovieId = show.vistaMovieId;
          const movieTitle = show.movieTitle;
          
          if (!movieId) return;
          
          // Use movieId as the key, but store both IDs
          if (!movieMap.has(movieId)) {
            movieMap.set(movieId, {
              movieId,
              vistaMovieId,
              movieTitle,
              show,
            });
          }
        });

        if (movieMap.size === 0) {
          setCinemaMovies([]);
          return;
        }

        // Create maps for quick lookup
        const moviesByIdMap = new Map(movies.map((m: any) => [m.id, m]));
        const moviesByVistaIdMap = new Map(
          movies
            .filter((m: any) => m.vistaMovieId || m.vistaMovieID)
            .map((m: any) => [m.vistaMovieId || m.vistaMovieID, m])
        );

        // Build the cinema movies list
        const cinemaMoviesList = Array.from(movieMap.values())
          .map((movieInfo: any) => {
            // Try to find movie by local ID first
            let movie = moviesByIdMap.get(movieInfo.movieId);
            
            // If not found and we have a vistaMovieId, try finding by Vista ID
            if (!movie && movieInfo.vistaMovieId) {
              movie = moviesByVistaIdMap.get(movieInfo.vistaMovieId);
            }
            
            // If found in local DB, use it
            if (movie) {
              return movie;
            }
            
            // Otherwise, create a placeholder from show data
            return {
              id: movieInfo.movieId,
              title: movieInfo.movieTitle || movieInfo.vistaMovieId || movieInfo.movieId,
              vistaMovieId: movieInfo.vistaMovieId || movieInfo.movieId,
            };
          })
          .filter((m: any) => m !== null && m.title);

        // Sort by title for better UX
        cinemaMoviesList.sort((a: any, b: any) => {
          const titleA = (a.title || '').toLowerCase();
          const titleB = (b.title || '').toLowerCase();
          return titleA.localeCompare(titleB);
        });

        setCinemaMovies(cinemaMoviesList);
      } else {
        setCinemaMovies([]);
      }
    } catch (error: any) {
      console.error('Error loading cinema movies:', error);
      setCinemaMovies([]);
    } finally {
      setLoadingCinemaMovies(false);
    }
  };

  const loadShows = async () => {
    try {
      setLoadingShows(true);
      console.log('Loading shows for:', {
        cinemaId: formData.cinemaId,
        movieId: formData.movieId,
        date: formData.showDate,
      });
      
      const response = await adminService.getShows(
        formData.cinemaId,
        formData.movieId,
        formData.showDate
      );
      
      console.log('Shows response:', response);
      
      if (response.success && response.data) {
        const showsData = Array.isArray(response.data) ? response.data : [];
        
        // Filter shows by the selected date (backup filtering in case API doesn't filter)
        const filteredShows = showsData.filter((show: any) => {
          if (!show.showDate) {
            return true; // Include if no date specified (shouldn't happen, but be safe)
          }
          return show.showDate === formData.showDate;
        });
        
        console.log('Setting shows:', filteredShows);
        console.log('First show details:', filteredShows.length > 0 ? {
          id: filteredShows[0].id,
          vistaShowId: filteredShows[0].vistaShowId,
          showTime: filteredShows[0].showTime,
          showDate: filteredShows[0].showDate,
          screenName: filteredShows[0].screenName,
          allKeys: Object.keys(filteredShows[0]),
        } : null);
        setShows(filteredShows);
        
        if (filteredShows.length === 0) {
          toast.error('No shows available for this movie and date');
        }
      } else {
        console.warn('Shows response not successful:', response);
        setShows([]);
        toast.error(response.message || 'Failed to load shows');
      }
    } catch (error: any) {
      console.error('Error loading shows:', error);
      toast.error(error.message || 'Failed to load shows');
      setShows([]);
    } finally {
      setLoadingShows(false);
    }
  };

  const loadSeatLayout = async (screenId: string) => {
    // Legacy method - kept for backward compatibility but not used
    // Use loadVistaSeatLayout instead
    loadDefaultSeatLayout();
  };


  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSeatSelect = (seatIds: string[]) => {
    if (!quantity) {
      return; // Don't allow seat selection if quantity not selected
    }
    
    // Enforce quantity limit
    if (seatIds.length > quantity) {
      toast.error(`You can only select ${quantity} seat${quantity > 1 ? 's' : ''}. Please deselect some seats.`);
      return;
    }
    
    // Check if all seats are from the same area
    if (seatIds.length > 0 && seatLayout && Array.isArray(seatLayout)) {
      const firstSeat = seatLayout
        .flatMap(row => row.seats)
        .find(seat => seat.id === seatIds[0]);
      
      if (firstSeat && firstSeat.areaCode) {
        const allSameArea = seatIds.every(seatId => {
          const seat = seatLayout
            .flatMap(row => row.seats)
            .find(s => s.id === seatId);
          return seat && seat.areaCode === firstSeat.areaCode;
        });
        
        if (!allSameArea) {
          toast.error('All seats must be selected from the same area.');
          return;
        }
      }
    }
    
    setSelectedSeats(seatIds);
    setFormData(prev => ({ ...prev, seats: seatIds.join(', ') }));
  };

  const handleConfirmSeats = async () => {
    if (!quantity || selectedSeats.length !== quantity || !selectedShow || !formData.cinemaId || !vistaTransactionId) {
      toast.error('Please select the correct number of seats');
      return;
    }

    // Get Vista IDs
    const cinema = cinemas.find(c => c.id === formData.cinemaId);
    const vistaCinemaId = cinema?.vistaCinemaId || cinema?.vistaCinemaID || cinema?.vistacinemaid;
    const vistaSessionId = selectedShow.vistaShowId || selectedShow.id;

    if (!vistaCinemaId || !vistaSessionId) {
      toast.error('Missing Vista IDs. Please try again.');
      return;
    }

    // Get seats from layout to get ticketTypeCode
    const seatsToUse: any[] = [];
    if (seatLayout && Array.isArray(seatLayout)) {
      selectedSeats.forEach(seatId => {
        for (const row of seatLayout) {
          const seat = row.seats.find((s: any) => s.id === seatId);
          if (seat) {
            seatsToUse.push(seat);
            break;
          }
        }
      });
    }

    if (seatsToUse.length === 0) {
      toast.error('Please select at least one seat');
      return;
    }

    // Get ticketTypeCode from first seat (all seats should have same area/ticketType)
    const ticketTypeCode = seatsToUse[0]?.ticketTypeCode;
    if (!ticketTypeCode) {
      // Log detailed error information for debugging
      console.error('Missing ticketTypeCode in selected seats:', {
        seatsToUse: seatsToUse.map(s => ({
          id: s.id,
          areaCode: s.areaCode,
          ticketTypeCode: s.ticketTypeCode,
          price: s.price,
        })),
        selectedShow: selectedShow ? {
          id: selectedShow.id,
          price: selectedShow.price,
          areaPricing: selectedShow.areaPricing,
        } : null,
        seatLayout: seatLayout ? seatLayout.slice(0, 2).map((row: { row: string; seats: any[] }) => ({
          row: row.row,
          seats: row.seats.slice(0, 2).map((s: any) => ({
            id: s.id,
            areaCode: s.areaCode,
            ticketTypeCode: s.ticketTypeCode,
          })),
        })) : null,
      });
      toast.error('Missing ticket type information. Please try selecting seats again.');
      return;
    }

    setIsConfirmingSeats(true);

    try {
      // Step 1: Add seats to Vista booking
      const addSeatsResponse = await adminService.addSeatsToVistaBooking(
        vistaCinemaId,
        String(vistaSessionId),
        vistaTransactionId,
        ticketTypeCode,
        quantity,
        true // userSelectedSeating
      );

      console.log('Add seats response:', addSeatsResponse);

      // Step 2: Build seats string in format "|<qty>|<seatId1>|<seatId2>|..."
      const seatIds = seatsToUse.map(seat => {
        // Priority: seatId (from API) > vistaSeatId (stored during transformation) > id (fallback)
        return seat.seatId || seat.vistaSeatId || seat.id;
      }).filter(Boolean);

      if (seatIds.length !== seatsToUse.length) {
        throw new Error('Some seats are missing seat IDs. Please try selecting seats again.');
      }

      // Build seats string: "|<qty>|<seatId1>|<seatId2>|..."
      const seatsString = `|${quantity}|${seatIds.join('|')}|`;

      // Step 3: Set seats in Vista booking
      const setSeatsResponse = await adminService.setSeatsInVistaBooking(
        vistaCinemaId,
        String(vistaSessionId),
        vistaTransactionId,
        seatsString
      );

      // Check if operation was successful
      // The response is wrapped in ApiResponse, so check the data property
      const vistaResponse = setSeatsResponse?.data;
      const isSuccess = setSeatsResponse?.success === true && (
        vistaResponse?.Success === true || 
        vistaResponse?.success === true ||
        vistaResponse === true
      );

      if (!isSuccess) {
        throw new Error(
          vistaResponse?.StrException || 
          vistaResponse?.info || 
          vistaResponse?.message ||
          setSeatsResponse?.message ||
          'Seat confirmation failed'
        );
      }

      // Seats confirmed successfully - show user details form
      setSeatsConfirmed(true);
      toast.success('Seats confirmed successfully! Please fill in customer details.');
    } catch (error: any) {
      console.error('Error confirming seats:', error);
      toast.error(error.message || 'Failed to confirm seats. Please try again.');
    } finally {
      setIsConfirmingSeats(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Use selectedShow from useMemo (defined at component level)
    // If not found, try to find it again with proper type conversion
    let currentSelectedShow: Show | null = selectedShow;
    if (!currentSelectedShow && formData.showId) {
      // Convert to strings for comparison (API returns numbers, formData has strings)
      const formShowIdStr = String(formData.showId);
      const found = shows.find(s => {
        const sIdStr = String(s.id);
        const sVistaShowIdStr = s.vistaShowId ? String(s.vistaShowId) : null;
        const sessionIdStr = String(s.vistaShowId || s.id);
        return sIdStr === formShowIdStr || 
               (sVistaShowIdStr && sVistaShowIdStr === formShowIdStr) ||
               sessionIdStr === formShowIdStr;
      });
      currentSelectedShow = found || null;
    }
    
    // Get selected seats count first
    const seatsArray = selectedSeats.length > 0 
      ? selectedSeats 
      : (formData.seats ? formData.seats.split(',').map(s => s.trim()).filter(s => s) : []);
    
    // Screen Name and Ticket Price should always come from selectedShow
    // They are auto-filled based on the show selection, not user input
    // Use selectedShow from useMemo if available, otherwise use currentSelectedShow
    // These values should ALWAYS come from the show object, never from formData
    const effectiveSelectedShow = selectedShow || currentSelectedShow;
    
    // Screen Name and Ticket Price MUST come from the selected show
    // These are required fields that must be present in the show data
    // We'll validate that they exist in the validation section below
    const screenName = effectiveSelectedShow?.screenName || '';
    
    // Calculate total ticket price based on selected seats' individual prices
    // Since price can be an object (area-wise) or number, we need to calculate from actual seat prices
    let totalTicketPrice = 0;
    if (seatLayout && Array.isArray(seatLayout) && seatsArray.length > 0) {
      seatsArray.forEach(seatId => {
        // Find the seat in the layout
        for (const row of seatLayout) {
          const seat = row.seats.find((s: any) => s.id === seatId);
          if (seat && seat.price && typeof seat.price === 'number') {
            totalTicketPrice += seat.price;
            break;
          }
        }
      });
    } else {
      // Fallback: use show price if available (for backward compatibility)
      const showPrice = effectiveSelectedShow?.price;
      if (typeof showPrice === 'number') {
        totalTicketPrice = showPrice * seatsArray.length;
      } else if (showPrice && typeof showPrice === 'object') {
        // If price is an object, use first area's price as fallback
        const firstPrice = Object.values(showPrice)[0];
        if (firstPrice && typeof firstPrice === 'object' && 'price' in firstPrice) {
          totalTicketPrice = (firstPrice as any).price * seatsArray.length;
        }
      }
    }
    
    console.log('Validation check:', {
      formDataShowId: formData.showId,
      selectedShowFromUseMemo: selectedShow ? { id: selectedShow.id, vistaShowId: selectedShow.vistaShowId, screenName: selectedShow.screenName, price: selectedShow.price } : null,
      currentSelectedShow: currentSelectedShow ? { id: currentSelectedShow.id, vistaShowId: currentSelectedShow.vistaShowId, screenName: currentSelectedShow.screenName, price: currentSelectedShow.price } : null,
      effectiveSelectedShow: effectiveSelectedShow ? { id: effectiveSelectedShow.id, vistaShowId: effectiveSelectedShow.vistaShowId, screenName: effectiveSelectedShow.screenName, price: effectiveSelectedShow.price } : null,
      screenName,
      seatsCount: seatsArray.length,
      totalTicketPrice,
    });
    
    // Detailed validation with specific error messages
    const missingFields: string[] = [];
    
    if (!formData.cinemaId || formData.cinemaId.trim() === '') {
      missingFields.push('Cinema');
    }
    if (!formData.movieId || formData.movieId.trim() === '') {
      missingFields.push('Movie');
    }
    if (!formData.showDate || formData.showDate.trim() === '') {
      missingFields.push('Show Date');
    }
    if (!formData.showId || formData.showId.trim() === '') {
      missingFields.push('Show Time');
    }
    
    // If show is not found, that's an error
    if (!effectiveSelectedShow && formData.showId) {
      console.error('Show not found even though showId is set:', {
        showId: formData.showId,
        showsAvailable: shows.map(s => ({ id: s.id, vistaShowId: s.vistaShowId, sessionId: s.vistaShowId || s.id })),
      });
      missingFields.push('Show Time (show not found - please refresh and try again)');
    }
    
    // If show IS selected, we MUST have screenName and price from the show
    // These are required fields that come from the show data, not user input
    if (effectiveSelectedShow) {
      // Screen Name must be available from the show
      if (!effectiveSelectedShow.screenName || effectiveSelectedShow.screenName.trim() === '') {
        console.error('Selected show has no screenName:', effectiveSelectedShow);
        missingFields.push('Screen Name (missing from show data)');
      }
      
      // Ticket Price must be available from the show and must be > 0
      const price =
      typeof effectiveSelectedShow.price === 'number'
        ? effectiveSelectedShow.price
        : Object.values(effectiveSelectedShow.price ?? {})[0]?.price ?? 0;
    
    if (price <= 0) {
      console.error('Selected show has no price or price is invalid:', effectiveSelectedShow);
      missingFields.push('Ticket Price (missing or invalid in show data)');
    }
    
    } else if (formData.showId) {
      // Show ID is set but show object not found - this is already handled above
      // But we still need to ensure screenName and price are not required if show is not found
      // (the "Show Time (show not found)" error will be shown instead)
    }
    
    // Seats validation
    if (seatsArray.length === 0) {
      missingFields.push('Seats (please select at least one seat)');
    }
    
    // Customer information validation
    if (!formData.customerName || formData.customerName.trim() === '') {
      missingFields.push('Customer Name');
    }
    if (!formData.customerEmail || formData.customerEmail.trim() === '') {
      missingFields.push('Customer Email');
    }
    if (!formData.customerPhone || formData.customerPhone.trim() === '') {
      missingFields.push('Customer Phone');
    }
    
    if (missingFields.length > 0) {
      console.error('Missing required fields:', missingFields);
      console.error('Form data:', {
        cinemaId: formData.cinemaId,
        movieId: formData.movieId,
        showDate: formData.showDate,
        showId: formData.showId,
        screenName: formData.screenName,
        screenNameFromShow: effectiveSelectedShow?.screenName,
        finalScreenName: screenName,
        seats: formData.seats,
        selectedSeats: selectedSeats,
        seatsArray: seatsArray,
        seatsCount: seatsArray.length,
        customerName: formData.customerName,
        customerEmail: formData.customerEmail,
        customerPhone: formData.customerPhone,
        ticketPrice: formData.ticketPrice,
        ticketPriceFromShow: effectiveSelectedShow?.price,
        totalTicketPrice: totalTicketPrice,
        effectiveSelectedShow: effectiveSelectedShow,
        showsCount: shows.length,
        allShows: shows.map(s => ({ 
          id: s.id, 
          vistaShowId: s.vistaShowId,
          sessionId: s.vistaShowId || s.id,
          screenName: s.screenName,
          price: s.price,
        })),
      });
      toast.error(`Please fill all required fields: ${missingFields.join(', ')}`);
      return;
    }
    
    // Update formData with screenName and ticketPrice from selectedShow (for display purposes)
    // Use effectiveSelectedShow which combines selectedShow and currentSelectedShow
    if (effectiveSelectedShow) {
      const showScreenName = effectiveSelectedShow.screenName || '';
      const showTicketPrice = effectiveSelectedShow.price ? effectiveSelectedShow.price.toString() : '';
      
      // Update formData if values are different (only if show data exists)
      if (showScreenName && formData.screenName !== showScreenName) {
        setFormData(prev => ({ ...prev, screenName: showScreenName }));
      }
      if (showTicketPrice && formData.ticketPrice !== showTicketPrice) {
        setFormData(prev => ({ ...prev, ticketPrice: showTicketPrice }));
      }
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.customerEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Validate phone (should be 10 digits)
    const phoneRegex = /^[0-9]{10}$/;
    if (!phoneRegex.test(formData.customerPhone.replace(/\D/g, ''))) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    try {
      setSubmitting(true);
      
      // Use effectiveSelectedShow that was found in validation
      if (!effectiveSelectedShow) {
        toast.error('Please select a valid show');
        return;
      }

      // seatsArray is already calculated above in validation
      // Calculate total amount (ticket price per seat * number of seats)
      const seatCount = seatsArray.length;
      const totalAmount = totalTicketPrice;

      // Get movie title from selected movie
      const selectedMovie = cinemaMovies.find(m => m.id === formData.movieId);
      const movieTitle = selectedMovie?.title || formData.movieId;

      // The show.id is already the Vista session ID (from showCode, vistaShowId, id, or code from Vista API)
      // Use the id field directly as it contains the Vista session ID
      const vistaShowId = formData.showId; // This is the Vista session ID from the show.id field
      
      if (!vistaShowId) {
        toast.error('Please select a valid show');
        return;
      }

      // Get Vista cinema ID from selected cinema
      const cinema = cinemas.find(c => c.id === formData.cinemaId);
      const vistaCinemaId = cinema?.vistaCinemaId || cinema?.vistaCinemaID || cinema?.vistacinemaid;
      
      if (!vistaCinemaId) {
        toast.error('Missing Vista cinema ID. Please select a valid cinema.');
        return;
      }
      
      console.log('Creating booking with final data:', {
        cinemaId: formData.cinemaId,
        vistaCinemaId: vistaCinemaId,
        movieId: formData.movieId,
        movieTitle,
        showId: vistaShowId,
        vistaTransactionId: vistaTransactionId,
        showDate: effectiveSelectedShow.showDate || formData.showDate,
        showTime: effectiveSelectedShow.showTime || '',
        screenName,
        screenId: effectiveSelectedShow.screenId || null,
        seats: seatsArray,
        totalAmount,
        customerName: formData.customerName,
        customerEmail: formData.customerEmail,
        customerPhone: formData.customerPhone,
      });
      
      const bookingData = {
        cinemaId: formData.cinemaId,
        vistaCinemaId: vistaCinemaId, // Vista cinema ID for commit API
        movieId: formData.movieId,
        movieTitle: movieTitle, // Include movie title for Vista-only movies
        showId: vistaShowId, // Vista session ID (this is the show.id from Vista API)
        vistaShowId: vistaShowId, // Vista session ID (alternative field name)
        vistaTransactionId: vistaTransactionId, // Vista transaction ID for commit API
        showDate: effectiveSelectedShow.showDate || formData.showDate,
        showTime: effectiveSelectedShow.showTime || '',
        screenName: screenName, // Auto-filled from selected show
        screenId: effectiveSelectedShow.screenId || null, // Screen ID if available
        seats: seatsArray, // Selected seats from seat layout
        customerName: formData.customerName,
        customerEmail: formData.customerEmail,
        customerPhone: formData.customerPhone.replace(/\D/g, ''), // Clean phone number
        ticketPrice: (totalTicketPrice / (seatsArray.length || 1)).toFixed(2), // Average price per seat (calculated from total)
        totalAmount: totalAmount, // Total amount (sum of individual seat prices)
      };

      const response = await adminService.createPendingBooking(bookingData);
      
      if (response.success) {
        toast.success('Booking created successfully! Payment link sent to customer.');
        onSuccess();
        handleClose();
      } else {
        toast.error(response.message || 'Failed to create booking');
      }
    } catch (error: any) {
      console.error('Error creating booking:', error);
      toast.error(error.response?.data?.message || 'Failed to create booking');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      cinemaId: '',
      movieId: '',
      showDate: new Date().toISOString().split('T')[0],
      showId: '',
      screenName: '',
      seats: '',
      customerName: '',
      customerEmail: '',
      customerPhone: '',
      ticketPrice: '',
    });
    setShows([]);
    setSeatLayout(null);
    setSelectedSeats([]);
    onHide();
  };

  // Calculate total amount (selectedShow is already defined above with useMemo)
  const totalAmount = formData.ticketPrice 
    ? (parseFloat(formData.ticketPrice) * (selectedSeats.length || (formData.seats ? formData.seats.split(',').filter(s => s.trim()).length : 0)))
    : 0;

  return (
    <Modal show={show} onHide={handleClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Create New Booking</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Cinema <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  name="cinemaId"
                  value={formData.cinemaId}
                  onChange={handleChange}
                  required
                  disabled={loading}
                >
                  <option value="">Select Cinema</option>
                  {cinemas.map((cinema) => (
                    <option key={cinema.id} value={cinema.id}>
                      {cinema.name} - {cinema.location || cinema.address}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Movie <span className="text-danger">*</span>
                </Form.Label>
                {loadingCinemaMovies ? (
                  <div className="d-flex align-items-center">
                    <Spinner animation="border" size="sm" className="me-2" />
                    <span>Loading movies...</span>
                  </div>
                ) : (
                  <Form.Select
                    name="movieId"
                    value={formData.movieId}
                    onChange={handleChange}
                    required
                    disabled={!formData.cinemaId || !formData.showDate}
                  >
                    <option value="">Select Movie</option>
                    {cinemaMovies.length > 0 ? (
                      cinemaMovies.map((movie) => (
                        <option key={movie.id} value={movie.id}>
                          {movie.title}
                        </option>
                      ))
                    ) : formData.cinemaId && formData.showDate ? (
                      <option value="" disabled>
                        No movies scheduled for this cinema
                      </option>
                    ) : (
                      <option value="" disabled>
                        Select cinema and date first
                      </option>
                    )}
                  </Form.Select>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Show Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  name="showDate"
                  value={formData.showDate}
                  onChange={handleChange}
                  required
                  min={new Date().toISOString().split("T")[0]}
                />
              </Form.Group>
            </Col>
            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Show Time <span className="text-danger">*</span>
                </Form.Label>
                {loadingShows ? (
                  <div className="d-flex align-items-center">
                    <Spinner animation="border" size="sm" className="me-2" />
                    <span>Loading shows...</span>
                  </div>
                ) : (
                  <Form.Select
                    name="showId"
                    value={formData.showId}
                    onChange={handleChange}
                    required
                    disabled={
                      !formData.cinemaId ||
                      !formData.movieId ||
                      !formData.showDate
                    }
                  >
                    <option value="">Select Show Time</option>
                    {shows.map((show) => {
                      // Use vistaShowId if available (Vista session ID), otherwise use id
                      // The id field should already contain the Vista session ID, but vistaShowId is explicit
                      // IMPORTANT: Convert to string because API returns numbers, but formData stores strings
                      const sessionId = String(show.vistaShowId || show.id);
                      // Get price display value
                      const priceDisplay =
                        typeof show.price === "number"
                          ? show.price
                          : show.price && typeof show.price === "object"
                            ? Object.values(show.price)[0]?.price || "N/A"
                            : "N/A";
                      return (
                        <option key={show.id} value={sessionId}>
                          {`${show.showTime} - ${show.screenName} (${show.screenType?.toUpperCase() || "REGULAR"}) - ₹${priceDisplay}`}
                        </option>
                      );
                    })}
                  </Form.Select>
                )}
                {formData.cinemaId &&
                  formData.movieId &&
                  formData.showDate &&
                  shows.length === 0 &&
                  !loadingShows && (
                    <Form.Text className="text-muted">
                      No shows available for this combination. Please try a
                      different date or movie.
                    </Form.Text>
                  )}
              </Form.Group>
            </Col>
          </Row>

          {/* Screen Name and Ticket Price - Always visible when show is selected */}
          {formData.showId && (
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Screen Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="screenName"
                    value={
                      selectedShow?.screenName ||
                      formData.screenName ||
                      "Not available"
                    }
                    readOnly
                    style={{
                      backgroundColor: "#f8f9fa",
                      cursor: "not-allowed",
                      fontWeight: "500",
                    }}
                  />
                  <Form.Text className="text-muted">
                    {selectedShow
                      ? "Auto-filled from selected show"
                      : "Please select a show"}
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Select Quantity <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="quantity"
                    value={quantity || ""}
                    onChange={(e) => {
                      const newQuantity = e.target.value
                        ? parseInt(e.target.value, 10)
                        : null;
                      setQuantity(newQuantity);
                      // Clear selected seats when quantity changes
                      if (newQuantity !== quantity) {
                        setSelectedSeats([]);
                        setFormData((prev) => ({ ...prev, seats: "" }));
                      }
                    }}
                    required
                  >
                    <option value="">Select quantity</option>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((qty) => (
                      <option key={qty} value={qty}>
                        {qty} {qty === 1 ? "ticket" : "tickets"}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    {selectedSeats.length > 0 &&
                      seatLayout &&
                      Array.isArray(seatLayout) && (
                        <>
                          Total: ₹
                          {(() => {
                            // Calculate total based on selected seats' individual prices
                            let total = 0;
                            selectedSeats.forEach((seatId) => {
                              // Find the seat in the layout
                              for (const row of seatLayout) {
                                const seat = row.seats.find(
                                  (s: any) => s.id === seatId,
                                );
                                if (seat && seat.price) {
                                  total += seat.price;
                                  break;
                                }
                              }
                            });
                            return total.toFixed(2);
                          })()}{" "}
                          ({selectedSeats.length} seat
                          {selectedSeats.length > 1 ? "s" : ""})
                        </>
                      )}
                    {quantity && selectedSeats.length === 0 && (
                      <>
                        {" "}
                        Please select {quantity} seat{quantity > 1 ? "s" : ""}{" "}
                        from the same area
                      </>
                    )}
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          )}

          {/* Seat Selection Section - Only show after quantity is selected */}
          {quantity &&
            seatLayout &&
            Array.isArray(seatLayout) &&
            seatLayout.length > 0 &&
            !seatsConfirmed && (
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Seat Selection <span className="text-danger">*</span>
                    </Form.Label>
                    {loadingSeatLayout || initializingBooking ? (
                      <div className="d-flex justify-content-center p-4">
                        <Spinner animation="border" />
                        <span className="ms-2">
                          {initializingBooking
                            ? "Initializing booking..."
                            : "Loading seat layout..."}
                        </span>
                      </div>
                    ) : (
                      <>
                        <SeatLayoutVisualizer
                          seatLayout={seatLayout}
                          selectedSeats={selectedSeats}
                          onSeatSelect={handleSeatSelect}
                          disabled={!quantity || isConfirmingSeats}
                          quantity={quantity ?? undefined}
                          onError={(msg) => toast.error(msg)}
                        />
                        {selectedSeats.length === quantity && (
                          <div className="mt-3">
                            <Button
                              variant="primary"
                              onClick={handleConfirmSeats}
                              disabled={
                                isConfirmingSeats ||
                                selectedSeats.length !== quantity
                              }
                            >
                              {isConfirmingSeats ? (
                                <>
                                  <Spinner
                                    animation="border"
                                    size="sm"
                                    className="me-2"
                                  />
                                  Confirming Seats...
                                </>
                              ) : (
                                "Confirm Seats & Continue"
                              )}
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                  </Form.Group>
                </Col>
              </Row>
            )}

          {/* Fallback: Text input for seats if layout not available */}
          {selectedShow && !seatLayout && (
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Seats <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="seats"
                    value={formData.seats}
                    onChange={handleChange}
                    placeholder="e.g., A1, A2, B3"
                    required
                  />
                  <Form.Text className="text-muted">
                    Enter seat numbers separated by commas (e.g., A1, A2, B3)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          )}

          {/* Customer Details - Only show after seats are confirmed */}
          {seatsConfirmed && (
            <>
              <hr />
              <h6 className="mb-3">Customer Details</h6>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Customer Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="customerName"
                      value={formData.customerName}
                      onChange={handleChange}
                      placeholder="Full Name"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Email <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="email"
                      name="customerEmail"
                      value={formData.customerEmail}
                      onChange={handleChange}
                      placeholder="customer@example.com"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Phone Number <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="tel"
                      name="customerPhone"
                      value={formData.customerPhone}
                      onChange={handleChange}
                      placeholder="10-digit phone number"
                      maxLength={10}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Creating...
              </>
            ) : (
              "Create Booking"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AddBookingModal;
